import { useCallback, useEffect } from "react";

import { axios, getBackendApiErrorResponse } from "~/core/axios";
import { identifyUserForAnalytics } from "~/core/posthog/browser-client";

import useUserSession from "./use-user-session";

export const sessionStorageUserLoggedInFlag = "___user_logged_in_flag___";

const setSessionStorageUserLoggedInFlag = () => {
  sessionStorage.setItem(sessionStorageUserLoggedInFlag, "true");
};

const getSessionStorageUserLoggedInFlag = () => {
  return sessionStorage.getItem(sessionStorageUserLoggedInFlag);
};

export const useUserLoggedIn = () => {
  const { userSession } = useUserSession();

  const notifySlackChannel = useCallback(async () => {
    try {
      await axios.post("/auth/send-new-login-notification");
      setSessionStorageUserLoggedInFlag();
    } catch (error) {
      const err = getBackendApiErrorResponse(error);
      throw new Error(
        err.data.message || "Could not post message to Slack workspace channel",
      );
    }
  }, []);

  useEffect(() => {
    if (!userSession) return;

    const checkUserLoggedIn = async () => {
      const isUserLoggedIn = getSessionStorageUserLoggedInFlag() === "true";

      if (!isUserLoggedIn) {
        await notifySlackChannel(); // Notify Slack
      }
    };

    checkUserLoggedIn(); // Call the function to check user login
  }, [notifySlackChannel, userSession]);

  useEffect(() => {
    const updateAnalyticalUser = async () => {
      if (!userSession || !userSession?.user?.email) return;

      await identifyUserForAnalytics({
        email: userSession?.user?.email,
        name: userSession?.user?.display_name,
        user_id: userSession?.user?.user_id,
        domain: userSession?.organizations?.[0]?.domain,
        workspace_id: userSession?.organizations?.[0]?.org_id,
      });
    };

    updateAnalyticalUser(); // Call the function to check user login
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession]);
};
